import { forwardRef, useEffect, useRef, useState } from "react";
import { vocabularize } from "src/Common/vocab";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

export const DATE_RANGE_OPTIONS = [
  { key: "past7", label: "Past 7 Days", days: 7 },
  { key: "past30", label: "Past 30 Days", days: 30 },
  { key: "pastQuarter", label: "Past Quarter", days: 90 },
  { key: "custom", label: "Custom Range", days: null },
];

const formatDisplay = (date) => (date ? moment(date).format("MMM D, YYYY") : "");

// Calendar icon from the design.
const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="18" viewBox="0 0 17 18" fill="none">
    <path d="M4.16667 3.95833V0.625C4.16667 0.279822 4.44649 0 4.79167 0C5.13684 0 5.41667 0.279822 5.41667 0.625V3.95833C5.41667 4.30351 5.13684 4.58333 4.79167 4.58333C4.44649 4.58333 4.16667 4.30351 4.16667 3.95833Z" fill="#B4BAD5" />
    <path d="M10.8333 3.95833V0.625C10.8333 0.279822 11.1132 0 11.4583 0C11.8035 0 12.0833 0.279822 12.0833 0.625V3.95833C12.0833 4.30351 11.8035 4.58333 11.4583 4.58333C11.1132 4.58333 10.8333 4.30351 10.8333 3.95833Z" fill="#B4BAD5" />
    <path d="M15 3.95833C15 3.38304 14.5336 2.91667 13.9583 2.91667H2.29167C1.71637 2.91667 1.25 3.38304 1.25 3.95833V15.625C1.25 16.2003 1.71637 16.6667 2.29167 16.6667H13.9583C14.5336 16.6667 15 16.2003 15 15.625V3.95833ZM16.25 15.625C16.25 16.8907 15.224 17.9167 13.9583 17.9167H2.29167C1.02601 17.9167 0 16.8907 0 15.625V3.95833C0 2.69268 1.02601 1.66667 2.29167 1.66667H13.9583C15.224 1.66667 16.25 2.69268 16.25 3.95833V15.625Z" fill="#B4BAD5" />
    <path d="M15.625 6.66667C15.9702 6.66667 16.25 6.94649 16.25 7.29167C16.25 7.63684 15.9702 7.91667 15.625 7.91667H0.625C0.279822 7.91667 0 7.63684 0 7.29167C0 6.94649 0.279822 6.66667 0.625 6.66667H15.625Z" fill="#B4BAD5" />
  </svg>
);

/**
 * Date-range selector for the Generate Report modal.
 *
 * selected: one of "past7" | "past30" | "pastQuarter" | "custom"
 * onSelect(key): preset toggle handler
 * customStart / customEnd: Date objects used when selected === "custom"
 * onCustomChange([start, end]): range picker handler
 * inline:    lay the preset chips and the range field out on a single row
 *            instead of stacking them. For full-width pages, where the modal's
 *            stacked layout wastes vertical space.
 * asDropdown: collapse the presets into one select instead of a row of chips.
 *            Chips make every option visible at a glance, which is worth a row
 *            in a modal; on a dense page they are mostly empty width.
 */
const DateRangeSelector = ({
  selected,
  onSelect,
  customStart,
  customEnd,
  onCustomChange,
  includeAllOption,
  inline = false,
  asDropdown = false,
  presets,
  rangeStart,
  rangeEnd,
}) => {
  // "All" (no date filter) is only offered for reports whose service supports it.
  // `presets` lets a caller supply its own set — Time Management needs
  // cycle-anchored weeks, which a rolling `days` count cannot express.
  const base = presets || DATE_RANGE_OPTIONS;
  const options = includeAllOption ? [{ key: "all", label: "All", days: null }, ...base] : base;

  // For presets, derive the displayed range from "today".
  const presetOption = options.find((option) => option.key === selected);
  const isCustom = selected === "custom";
  const isAll = selected === "all";

  let displayStart = customStart;
  let displayEnd = customEnd;
  if (!isCustom && presetOption?.days) {
    displayEnd = new Date();
    displayStart = moment().subtract(presetOption.days, "days").toDate();
  }
  // An explicit range wins over anything derived: a preset whose bounds the
  // caller computed (a pay week, say) cannot be recovered from a day count.
  if (!isCustom && (rangeStart || rangeEnd)) {
    displayStart = rangeStart;
    displayEnd = rangeEnd;
  }

  const displayText = isAll
    ? vocabularize("All work orders (no date filter)")
    : displayStart || displayEnd
    ? `${formatDisplay(displayStart)} - ${formatDisplay(displayEnd)}`
    : "Select a date range";

  // Read-only range display field (40px, #F8F9FE) shown below the preset chips.
  const RangeInput = forwardRef(({ onClick }, ref) => (
    <div
      ref={ref}
      onClick={isCustom ? onClick : undefined}
      className={`w-full h-10 rounded-[6px] outline outline-[1px] outline-offset-[-1px] outline-[#CBD5E1] bg-[#F8F9FE] flex items-center gap-3 px-3 overflow-hidden ${
        isCustom ? "cursor-pointer" : "cursor-default"
      }`}
    >
      <span className="flex-1 font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#5F6477]">
        {displayText}
      </span>
      <CalendarIcon />
    </div>
  ));

  if (asDropdown) {
    return (
      <div className="w-full flex flex-wrap items-center gap-2">
        <PresetDropdown options={options} selected={selected} onSelect={onSelect} />
        <div className="w-[250px] shrink-0">
          {isCustom ? (
            <DatePicker
              selectsRange
              startDate={customStart}
              endDate={customEnd}
              onChange={onCustomChange}
              customInput={<RangeInput />}
            />
          ) : (
            <RangeInput />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full flex ${inline ? "flex-wrap items-center gap-2" : "flex-col gap-3"}`}>
      <div className={`flex flex-wrap gap-2 ${inline ? "flex-1 min-w-[300px]" : "w-full"}`}>
        {options.map((option) => {
          const isSelected = option.key === selected;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => onSelect(option.key)}
              className={`flex-1 rounded-[10px] ring-1 ring-inset flex items-center justify-center text-[14px] leading-[20px] tracking-[0.14px] text-center transition-colors ${
                inline ? "min-w-[84px] h-10 px-2" : "min-w-[110px] p-[10px]"
              } ${
                isSelected
                  ? "bg-[#EAF3FF] ring-[#0065FD] text-[#0065FD] font-OpenSansSemiBold capitalize"
                  : "bg-white ring-[#CBD5E1] text-[#5F6477] font-OpenSansRegular hover:bg-[#F7F9FC]"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      <div className={inline ? "w-[250px] shrink-0" : "w-full"}>
        {isCustom ? (
          <DatePicker
            selectsRange
            startDate={customStart}
            endDate={customEnd}
            onChange={onCustomChange}
            customInput={<RangeInput />}
          />
        ) : (
          <RangeInput />
        )}
      </div>
    </div>
  );
};

/**
 * Single-select list of the date presets. Written out rather than using a
 * native <select> so it matches the filter inputs beside it — a browser-styled
 * select is conspicuously different next to them.
 */
const PresetDropdown = ({ options, selected, onSelect }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const current = options.find((option) => option.key === selected);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative w-[200px] shrink-0">
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full h-10 px-3 rounded-[6px] bg-white outline outline-[1px] outline-offset-[-1px] outline-[#CBD5E1] flex items-center justify-between gap-2 hover:bg-[#F7F9FC]"
      >
        <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] truncate">
          {current?.label || "Select a range"}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
          className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path d="M2.5 4.5L6 8l3.5-3.5" stroke="#5F6477" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open ? (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-full mt-1 z-30 py-1 bg-white rounded-[8px] border border-[#C9CEE2] shadow-[0px_8px_24px_rgba(16,24,40,0.12)]"
        >
          {options.map((option) => {
            const isSelected = option.key === selected;
            return (
              <button
                key={option.key}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onSelect(option.key);
                  setOpen(false);
                }}
                className={`w-full px-3 py-[7px] text-left text-[14px] leading-[20px] tracking-[0.14px] hover:bg-[#EEF0F9] ${
                  isSelected ? "bg-[#EAF3FF] text-[#0058E5] font-OpenSansSemiBold" : "text-[#1A2332] font-OpenSansRegular"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default DateRangeSelector;
